/**
 * Sales Data lead/call status → qualification bucket.
 *
 * Source-side status (GHL appointment status) collapses to two display buckets:
 *   - `cancelled` / `noshow` / `invalid` → "unqualified"
 *   - everything else (`confirmed`, `showed`, ...) → "qualified"
 * Used by both the leads endpoint (status filter) and the Sales Data UI (badge label).
 */

export type LeadQualification = "qualified" | "unqualified";

// Raw GHL statuses (lowercased; both spelling variants) that count as "unqualified".
export const UNQUALIFIED_STATUSES: ReadonlySet<string> = new Set([
  "cancelled",
  "canceled",
  "noshow",
  "no-show",
  "no_show",
  "invalid",
]);

/**
 * Map a raw source status to a qualification bucket. Returns null when there is no
 * status (non-appointment conversions like leads/purchases) so the UI renders "—".
 */
export function qualificationOf(status: string | null | undefined): LeadQualification | null {
  if (!status) return null;
  return UNQUALIFIED_STATUSES.has(status.trim().toLowerCase()) ? "unqualified" : "qualified";
}

/**
 * Raw statuses that belong to a given qualification bucket. Used to translate a UI
 * status filter (qualified/unqualified) into a DB `status IN (...)` clause.
 * `qualified` = "not in the unqualified set", so it can't be expressed as a finite
 * IN-list — callers filter it as `status NOT IN (unqualified)` instead (see endpoint).
 */
export function unqualifiedStatusList(): string[] {
  return [...UNQUALIFIED_STATUSES];
}
